import type { JSX } from 'react';
import {
  Node,
  mergeAttributes,
  ReactNodeViewRenderer,
  NodeViewWrapper,
  NodeViewContent,
  type JSONContent,
  type NodeViewProps,
} from '@tiptap/react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { toEmbedTarget } from '../lib/embed';
import { INNER_CONTENT } from './layout-nodes';
import { useIsFileDeleted } from './deleted-files';

/**
 * Блоки фазы 1.7 — сериализация в .md fenced-блоками (см. markdown-html.ts):
 *   ```embed  {"url":"https://…","height":480}   → <div class="fk-embed" data-embed data-height><a>…</a></div>
 *   ```tabs   секции `## Название`               → <div class="fk-tabs" data-tabs><div class="fk-tab" data-title>…</div></div>
 *   ```button {"label","href","style"}            → <a class="fk-button fk-button-primary" data-button href>label</a>
 *   ```file   {"url","name","size"}               → <a class="fk-file" data-file data-name data-size href>…</a>
 *   ```kpi    {"code"}                            → <div class="fk-kpi" data-kpi="code"></div>
 * iframe в HTML не хранится: читалка сама строит его по allow-list (lib/embed.ts).
 */

export const FkEmbed = Node.create({
  name: 'fkEmbed',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      url: { default: '', parseHTML: (el: HTMLElement) => el.getAttribute('data-embed') || '', renderHTML: () => ({}) },
      height: { default: 480, parseHTML: (el: HTMLElement) => parseInt(el.getAttribute('data-height') || '480', 10) || 480, renderHTML: () => ({}) },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-embed]' }];
  },
  renderHTML({ HTMLAttributes, node }) {
    const url = String(node.attrs.url || '');
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: 'fk-embed', 'data-embed': url, 'data-height': String(node.attrs.height || 480) }),
      ['a', { href: url, target: '_blank', rel: 'noopener noreferrer' }, url],
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(EmbedView);
  },
});

function EmbedView(props: NodeViewProps): JSX.Element {
  const { node, updateAttributes, deleteNode, selected } = props;
  const url = String(node.attrs.url || '');
  const height = Number(node.attrs.height) || 480;
  const target = toEmbedTarget(url);
  return (
    <NodeViewWrapper className="fk-layout-node" data-selected={selected}>
      <div className="fk-layout-bar" contentEditable={false}>
        <span>Встраивание</span>
        <input className="fk-layout-input" value={url} onChange={(e) => updateAttributes({ url: e.target.value })} placeholder="https://…" style={{ fontWeight: 500 }} />
        <input className="fk-layout-input" type="number" min={200} max={1200} step={20} value={height} onChange={(e) => updateAttributes({ height: Number(e.target.value) || 480 })} style={{ flex: '0 0 90px', fontWeight: 500 }} title="Высота, px" />
        <button type="button" onClick={() => deleteNode()} title="Удалить блок" className="danger"><DeleteOutlined /></button>
      </div>
      {target ? (
        <iframe src={target.src} style={{ width: '100%', height, border: 0, borderRadius: 12, background: 'var(--fk-bg-subtle)' }} loading="lazy" allowFullScreen sandbox="allow-scripts allow-same-origin allow-popups allow-forms" title="Встраивание" />
      ) : (
        <div className="fk-video-processing" style={{ aspectRatio: 'auto', minHeight: 120 }}>
          {url ? 'Этот адрес встроить нельзя — поддерживаются YouTube, RuTube, VK Видео, Google Docs/Sheets, Figma, Miro, Яндекс.Карты. В статье останется ссылка.' : 'Вставьте ссылку на видео, документ или доску'}
        </div>
      )}
    </NodeViewWrapper>
  );
}

export const FkTabs = Node.create({
  name: 'fkTabs',
  group: 'block',
  content: 'fkTab+',
  defining: true,
  isolating: true,
  parseHTML() {
    return [{ tag: 'div[data-tabs]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { class: 'fk-tabs', 'data-tabs': '' }), 0];
  },
  addNodeView() {
    return ReactNodeViewRenderer(TabsView);
  },
});

export const FkTab = Node.create({
  name: 'fkTab',
  content: INNER_CONTENT,
  defining: true,
  isolating: true,
  addAttributes() {
    return {
      title: {
        default: 'Вкладка',
        parseHTML: (el: HTMLElement) => el.getAttribute('data-title') || el.querySelector('.fk-tab-title')?.textContent?.trim() || 'Вкладка',
        renderHTML: () => ({}),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'div.fk-tab', contentElement: '.fk-tab-body' }];
  },
  renderHTML({ HTMLAttributes, node }) {
    const title = String(node.attrs.title || 'Вкладка');
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: 'fk-tab', 'data-title': title }),
      ['div', { class: 'fk-tab-title' }, title],
      ['div', { class: 'fk-tab-body' }, 0],
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(TabView);
  },
});

function TabsView(props: NodeViewProps): JSX.Element {
  const { node, editor, getPos, deleteNode } = props;
  const addTab = (): void => {
    const pos = getPos();
    editor
      .chain()
      .focus()
      .insertContentAt(pos + node.nodeSize - 1, { type: 'fkTab', attrs: { title: `Вкладка ${node.childCount + 1}` }, content: [{ type: 'paragraph' }] })
      .run();
  };
  return (
    <NodeViewWrapper className="fk-layout-node">
      <div className="fk-layout-bar" contentEditable={false}>
        <span>Вкладки: {node.childCount} (в редакторе показаны все)</span>
        <button type="button" onClick={addTab} title="Добавить вкладку"><PlusOutlined /></button>
        <button type="button" onClick={() => deleteNode()} title="Удалить блок" className="danger"><DeleteOutlined /></button>
      </div>
      <NodeViewContent className="fk-tabs fk-tabs-edit" />
    </NodeViewWrapper>
  );
}

function TabView(props: NodeViewProps): JSX.Element {
  const { node, updateAttributes, deleteNode } = props;
  return (
    <NodeViewWrapper className="fk-tab fk-tab-edit">
      <div className="fk-step-title-row" contentEditable={false}>
        <input className="fk-layout-input" value={String(node.attrs.title ?? '')} onChange={(e) => updateAttributes({ title: e.target.value })} placeholder="Название вкладки" />
        <button type="button" onClick={() => deleteNode()} title="Удалить вкладку" className="fk-layout-btn danger"><DeleteOutlined /></button>
      </div>
      <NodeViewContent className="fk-tab-body" />
    </NodeViewWrapper>
  );
}

export const FkButton = Node.create({
  name: 'fkButton',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      label: { default: 'Открыть', parseHTML: (el: HTMLElement) => el.textContent?.trim() || 'Открыть', renderHTML: () => ({}) },
      href: { default: '', parseHTML: (el: HTMLElement) => el.getAttribute('href') || '', renderHTML: () => ({}) },
      style: { default: 'primary', parseHTML: (el: HTMLElement) => (el.classList.contains('fk-button-default') ? 'default' : 'primary'), renderHTML: () => ({}) },
    };
  },
  parseHTML() {
    return [{ tag: 'a[data-button]' }];
  },
  renderHTML({ HTMLAttributes, node }) {
    const style = node.attrs.style === 'default' ? 'default' : 'primary';
    return [
      'a',
      mergeAttributes(HTMLAttributes, { class: `fk-button fk-button-${style}`, 'data-button': '', href: String(node.attrs.href || '#'), rel: 'noopener' }),
      String(node.attrs.label || 'Открыть'),
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ButtonView);
  },
});

function ButtonView(props: NodeViewProps): JSX.Element {
  const { node, updateAttributes, deleteNode } = props;
  const style = node.attrs.style === 'default' ? 'default' : 'primary';
  return (
    <NodeViewWrapper className="fk-layout-node">
      <div className="fk-layout-bar" contentEditable={false}>
        <span>Кнопка</span>
        <input className="fk-layout-input" value={String(node.attrs.label ?? '')} onChange={(e) => updateAttributes({ label: e.target.value })} placeholder="Текст кнопки" style={{ flex: '0 0 200px' }} />
        <input className="fk-layout-input" value={String(node.attrs.href ?? '')} onChange={(e) => updateAttributes({ href: e.target.value })} placeholder="https://… или /article/…" style={{ fontWeight: 500 }} />
        <select className="fk-layout-input" value={style} onChange={(e) => updateAttributes({ style: e.target.value })} style={{ flex: '0 0 130px', fontWeight: 500 }}>
          <option value="primary">Оранжевая</option>
          <option value="default">Контурная</option>
        </select>
        <button type="button" onClick={() => deleteNode()} title="Удалить" className="danger"><DeleteOutlined /></button>
      </div>
      <span className={`fk-button fk-button-${style}`} contentEditable={false}>{String(node.attrs.label || 'Открыть')}</span>
    </NodeViewWrapper>
  );
}

export const FkFile = Node.create({
  name: 'fkFile',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      url: { default: '', parseHTML: (el: HTMLElement) => el.getAttribute('href') || '', renderHTML: () => ({}) },
      name: { default: 'Файл', parseHTML: (el: HTMLElement) => el.getAttribute('data-name') || 'Файл', renderHTML: () => ({}) },
      size: { default: 0, parseHTML: (el: HTMLElement) => parseInt(el.getAttribute('data-size') || '0', 10) || 0, renderHTML: () => ({}) },
    };
  },
  parseHTML() {
    return [{ tag: 'a[data-file]' }];
  },
  renderHTML({ HTMLAttributes, node }) {
    const name = String(node.attrs.name || 'Файл');
    const size = Number(node.attrs.size) || 0;
    return [
      'a',
      mergeAttributes(HTMLAttributes, { class: 'fk-file', 'data-file': '', 'data-name': name, 'data-size': String(size), href: String(node.attrs.url || '#'), target: '_blank', rel: 'noopener' }),
      ['span', { class: 'fk-file-icon' }, fileIcon(name)],
      ['span', { class: 'fk-file-name' }, name],
      ['span', { class: 'fk-file-size' }, formatSize(size)],
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(FileView);
  },
});

function FileView(props: NodeViewProps): JSX.Element {
  const { node, updateAttributes, deleteNode, selected } = props;
  const name = String(node.attrs.name || 'Файл');
  const url = String(node.attrs.url || '');
  // Файл удалили на Диске (он в Корзине) или удалили вложение: карточку оставляем как есть,
  // но помечаем и даём «Убрать». Текст статьи сам не меняется — убирает человек.
  const deleted = useIsFileDeleted(url);
  if (deleted) {
    return (
      <NodeViewWrapper className="fk-layout-node" data-selected={selected}>
        <div className="fk-file fk-file-deleted" contentEditable={false} style={{ outline: selected ? '2px solid var(--fk-orange)' : undefined }}>
          <span className="fk-file-icon">🚫</span>
          <span className="fk-file-name">{name}</span>
          <span className="fk-file-size">{url.includes('/disk/files/') ? 'Файл удалён с Диска' : 'Файл удалён'}</span>
          <button type="button" onClick={() => deleteNode()} title="Убрать карточку из статьи" className="fk-layout-btn danger">Убрать</button>
        </div>
      </NodeViewWrapper>
    );
  }
  return (
    <NodeViewWrapper className="fk-layout-node" data-selected={selected}>
      <div className="fk-file" contentEditable={false} style={{ outline: selected ? '2px solid var(--fk-orange)' : undefined }}>
        <span className="fk-file-icon">{fileIcon(name)}</span>
        <input className="fk-layout-input" value={name} onChange={(e) => updateAttributes({ name: e.target.value })} style={{ fontWeight: 600 }} />
        <span className="fk-file-size">{formatSize(Number(node.attrs.size) || 0)}</span>
        <button type="button" onClick={() => deleteNode()} title="Удалить" className="fk-layout-btn danger"><DeleteOutlined /></button>
      </div>
    </NodeViewWrapper>
  );
}

export const FkKpi = Node.create({
  name: 'fkKpi',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      code: { default: '', parseHTML: (el: HTMLElement) => el.getAttribute('data-kpi') || '', renderHTML: () => ({}) },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-kpi]' }];
  },
  renderHTML({ HTMLAttributes, node }) {
    return ['div', mergeAttributes(HTMLAttributes, { class: 'fk-kpi', 'data-kpi': String(node.attrs.code || '') }), ['span', { class: 'fk-kpi-code' }, `KPI: ${String(node.attrs.code || '')}`]];
  },
  addNodeView() {
    return ReactNodeViewRenderer(KpiView);
  },
});

function KpiView(props: NodeViewProps): JSX.Element {
  const { node, updateAttributes, deleteNode } = props;
  return (
    <NodeViewWrapper className="fk-layout-node">
      <div className="fk-layout-bar" contentEditable={false}>
        <span>KPI-виджет</span>
        <input className="fk-layout-input" value={String(node.attrs.code ?? '')} onChange={(e) => updateAttributes({ code: e.target.value })} placeholder="код KPI, напр. cogs_pct" style={{ fontWeight: 500 }} />
        <button type="button" onClick={() => deleteNode()} title="Удалить" className="danger"><DeleteOutlined /></button>
      </div>
      <div className="fk-kpi-placeholder">📈 В статье покажется виджет KPI «{String(node.attrs.code || '…')}» со ссылкой на дашборд</div>
    </NodeViewWrapper>
  );
}

/**
 * «Проверь себя» (фаза 2.1): вопрос с вариантами внутри урока/статьи.
 * Сериализация — ```check {"q","options","correct","explanation"}. В HTML
 * правильный индекс лежит в data-correct (это самопроверка, не зачёт).
 */
export const FkCheck = Node.create({
  name: 'fkCheck',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      q: {
        default: '',
        parseHTML: (el: HTMLElement) => (el.querySelector('.fk-check-q')?.textContent || '').trim(),
        renderHTML: () => ({}),
      },
      options: {
        default: ['', ''],
        parseHTML: (el: HTMLElement) =>
          Array.from(el.querySelectorAll('.fk-check-options > li')).map((li) => (li.textContent || '').trim()),
        renderHTML: () => ({}),
      },
      correct: {
        default: 0,
        parseHTML: (el: HTMLElement) => parseInt(el.getAttribute('data-correct') || '0', 10) || 0,
        renderHTML: () => ({}),
      },
      explanation: {
        default: '',
        parseHTML: (el: HTMLElement) => el.getAttribute('data-explanation') || '',
        renderHTML: () => ({}),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-check]' }];
  },
  renderHTML({ HTMLAttributes, node }) {
    const options: string[] = Array.isArray(node.attrs.options) ? node.attrs.options.map((o: unknown) => String(o ?? '')) : [];
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        class: 'fk-check',
        'data-check': '',
        'data-correct': String(Number(node.attrs.correct) || 0),
        'data-explanation': String(node.attrs.explanation || ''),
      }),
      ['div', { class: 'fk-check-q' }, String(node.attrs.q || '')],
      ['ol', { class: 'fk-check-options' }, ...options.map((o) => ['li', { class: 'fk-check-option' }, o] as [string, Record<string, string>, string])],
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(CheckView);
  },
});

function CheckView(props: NodeViewProps): JSX.Element {
  const { node, updateAttributes, deleteNode } = props;
  const options: string[] = Array.isArray(node.attrs.options) ? node.attrs.options : [];
  const correct = Number(node.attrs.correct) || 0;
  const setOptions = (next: string[]): void => updateAttributes({ options: next, correct: Math.min(correct, Math.max(0, next.length - 1)) });
  return (
    <NodeViewWrapper className="fk-layout-node fk-check-edit">
      <div className="fk-layout-bar" contentEditable={false}>
        <span>Проверь себя</span>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={() => deleteNode()} title="Удалить" className="danger"><DeleteOutlined /></button>
      </div>
      <div className="fk-check-form" contentEditable={false}>
        <input className="fk-layout-input" value={String(node.attrs.q ?? '')} onChange={(e) => updateAttributes({ q: e.target.value })} placeholder="Вопрос" style={{ width: '100%', fontWeight: 600 }} />
        {options.map((o, i) => (
          <div key={i} className="fk-check-form-row">
            <input type="radio" name={`fk-check-${props.getPos?.() ?? 'x'}`} checked={correct === i} onChange={() => updateAttributes({ correct: i })} title="Правильный ответ" />
            <input className="fk-layout-input" value={o} onChange={(e) => setOptions(options.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`Вариант ${i + 1}`} style={{ flex: 1 }} />
            <button type="button" className="danger" title="Убрать вариант" disabled={options.length <= 2} onClick={() => setOptions(options.filter((_, j) => j !== i))}><DeleteOutlined /></button>
          </div>
        ))}
        <div className="fk-check-form-row">
          <button type="button" disabled={options.length >= 8} onClick={() => setOptions([...options, ''])}><PlusOutlined /> Вариант</button>
          <input className="fk-layout-input" value={String(node.attrs.explanation ?? '')} onChange={(e) => updateAttributes({ explanation: e.target.value })} placeholder="Объяснение после ответа (необязательно)" style={{ flex: 1 }} />
        </div>
      </div>
    </NodeViewWrapper>
  );
}

export function fileIcon(name: string): string {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf') return '📕';
  if (['doc', 'docx', 'rtf'].includes(ext)) return '📘';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📗';
  if (['ppt', 'pptx'].includes(ext)) return '📙';
  if (['zip', 'rar', '7z'].includes(ext)) return '🗜️';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return '🖼️';
  if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) return '🎬';
  return '📄';
}

export function formatSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
}

/** Заготовки для slash-меню. */
export const MISC_TEMPLATES: Record<'tabs', JSONContent> = {
  tabs: {
    type: 'fkTabs',
    content: [
      { type: 'fkTab', attrs: { title: 'Вкладка 1' }, content: [{ type: 'paragraph' }] },
      { type: 'fkTab', attrs: { title: 'Вкладка 2' }, content: [{ type: 'paragraph' }] },
    ],
  },
};
